import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface EventReport {
  id: string;
  event_date: string;
  title: string;
  description: string;
  images: string[];
  created_at: string;
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [latestNews, setLatestNews] = useState<EventReport[]>([]);
  const [loadingNews, setLoadingNews] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchLatestNews();
  }, []);

  const fetchLatestNews = async () => {
    try {
      const { data, error } = await supabase
        .from('event_reports')
        .select('*')
        .order('event_date', { ascending: false })
        .limit(3);

      if (error) throw error;
      setLatestNews(data || []);
    } catch (error) {
      console.error('最新ニュースの取得に失敗しました:', error);
    } finally {
      setLoadingNews(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-4 md:py-6 px-4 md:px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold mb-1 tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>兵庫中央岳風会</h1>
              <p className="text-xs md:text-sm opacity-90 font-light">公益社団法人日本詩吟学院認可</p>
            </div>
            <div className="text-left md:text-right text-xs md:text-sm font-light">
              <p className="flex items-center gap-2">
                <i className="ri-map-pin-line"></i>
                〒653-0867
              </p>
              <p className="mt-1">神戸市長田区高東町1-2-26</p>
            </div>
          </div>
        </div>
        <nav className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <ul className="flex flex-wrap justify-center items-center gap-1 md:gap-6 py-3 md:py-4">
              <li><Link to="/" className="text-blue-800 font-bold border-b-2 border-blue-800 pb-1 text-xs md:text-sm px-2 md:px-3 py-1 whitespace-nowrap">トップページ</Link></li>
              <li><Link to="/about" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">会の紹介</Link></li>
              <li><Link to="/greeting" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">会長挨拶</Link></li>
              <li><Link to="/activities" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">活動計画</Link></li>
              <li><Link to="/membership" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">入会案内</Link></li>
              <li><Link to="/records" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">活動記録</Link></li>
              <li><Link to="/gallery" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">ギャラリー</Link></li>
            </ul>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-blue-800/10 text-blue-800 px-4 py-2 rounded-full text-xs md:text-sm font-medium mb-4 md:mb-6">
                令和8年度
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                伝統の詩吟を<br />次世代へ
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
                創立70周年を迎えた兵庫中央岳風会。<br />
                確立された歴史と伝統を活かし、<br className="md:hidden" />吟道の普及に努めています。
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Link 
                  to="/membership" 
                  className="inline-flex items-center justify-center bg-blue-800 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg hover:bg-blue-900 font-medium text-sm md:text-base transition-all hover:shadow-lg hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-user-add-line mr-2 text-lg"></i>
                  入会案内
                </Link>
                <Link 
                  to="/about" 
                  className="inline-flex items-center justify-center border-2 border-blue-800 text-blue-800 px-6 md:px-8 py-3 md:py-4 rounded-lg hover:bg-blue-800 hover:text-white font-medium text-sm md:text-base transition-all hover:shadow-lg whitespace-nowrap"
                >
                  <i className="ri-information-line mr-2 text-lg"></i>
                  会について
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-800/20 to-transparent rounded-2xl transform rotate-3"></div>
                <div className="relative w-full h-[280px] md:h-[400px] lg:h-[500px] overflow-hidden rounded-2xl shadow-2xl">
                  <img 
                    src="https://static.readdy.ai/image/905cb46d71d05500d75f536f30a9de59/7c0085004b2d4c72c60d674d27e02513.jpeg" 
                    alt="令和七年巳開運招福" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-block bg-blue-800/10 text-blue-800 px-4 py-2 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4">
              NEWS
            </div>
            <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>お知らせ</h3>
            <p className="text-sm md:text-base text-gray-600">最新のお知らせは随時更新いたします</p>
          </div>
          
          {loadingNews ? (
            <div className="text-center py-8">
              <i className="ri-loader-4-line text-3xl text-blue-800 animate-spin"></i>
              <p className="mt-2 text-gray-600 text-sm">読み込み中...</p>
            </div>
          ) : latestNews.length > 0 ? (
            <>
              <div className="space-y-4 mb-8">
                {latestNews.map((news) => (
                  <Link
                    key={news.id}
                    to="/news"
                    className="block bg-white rounded-xl p-5 md:p-6 shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-800"
                  >
                    <div className="flex flex-col md:flex-row gap-4">
                      {news.images && news.images.length > 0 && (
                        <div className="w-full md:w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={news.images[0]}
                            alt={news.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-blue-800 text-white text-xs px-3 py-1 rounded-full font-medium">
                            {formatDate(news.event_date)}
                          </span>
                          <span className="text-xs text-gray-500">
                            投稿日: {formatDate(news.created_at)}
                          </span>
                        </div>
                        <h4 className="text-base md:text-lg font-bold text-gray-900 mb-2 line-clamp-1">
                          {news.title}
                        </h4>
                        {news.description && (
                          <p className="text-xs md:text-sm text-gray-600 line-clamp-2">
                            {news.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center text-blue-800 text-sm font-medium md:ml-4">
                        <i className="ri-arrow-right-line text-xl"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/news"
                  className="inline-flex items-center bg-blue-800 text-white px-6 py-3 rounded-lg hover:bg-blue-900 font-medium text-sm transition-all hover:shadow-lg whitespace-nowrap"
                >
                  すべてのNEWSを見る
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-10">
              <Link 
                to="/news" 
                className="group bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-800"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className="ri-newspaper-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base md:text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-800 transition-colors">最新のNEWS</h4>
                    <p className="text-xs md:text-sm text-gray-600">新しいお知らせをご覧いただけます</p>
                  </div>
                </div>
                <div className="flex items-center text-blue-800 text-sm font-medium group-hover:translate-x-2 transition-transform">
                  詳しく見る
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>

              <Link 
                to="/news-archive" 
                className="group bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-800"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className="ri-archive-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base md:text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-800 transition-colors">過去のニュース</h4>
                    <p className="text-xs md:text-sm text-gray-600">これまでの活動記録をご覧いただけます</p>
                  </div>
                </div>
                <div className="flex items-center text-blue-800 text-sm font-medium group-hover:translate-x-2 transition-transform">
                  詳しく見る
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Slogan Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-block bg-blue-800/10 text-blue-800 px-4 py-2 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4">
              SLOGAN
            </div>
            <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>令和8年度スローガン</h3>
          </div>
          
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl shadow-xl p-6 md:p-12 text-white mb-8 md:mb-12">
            <div className="text-center mb-6 md:mb-8">
              <i className="ri-flag-line text-4xl md:text-5xl mb-4 block opacity-90"></i>
              <h2 className="text-lg md:text-2xl lg:text-3xl font-bold leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                先輩 先生方の築き上げた<br />
                伝統ある活動を継承し<br className="hidden md:block" />
                会員同士の絆を深めよう
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="bg-white border-2 border-gray-100 rounded-xl p-5 md:p-6 hover:border-blue-800 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-800/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-2xl text-blue-800"></i>
              </div>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                確立された歴史と伝統を活用、和して流れず、吟道普及・会員増強実現に努めよう。
              </p>
            </div>
            <div className="bg-white border-2 border-gray-100 rounded-xl p-5 md:p-6 hover:border-blue-800 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-800/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-team-line text-2xl text-blue-800"></i>
              </div>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                会員・吟友は、皆家族の気持に共鳴し仲良く相互扶助で過そう。
              </p>
            </div>
            <div className="bg-white border-2 border-gray-100 rounded-xl p-5 md:p-6 hover:border-blue-800 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-800/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-heart-pulse-line text-2xl text-blue-800"></i>
              </div>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                高齢化時代に備え、心身共に健康第一、日々吟道研鑽に精進しよう。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <Link 
              to="/activities" 
              className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all border-2 border-gray-100 hover:border-blue-800"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-800/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-800 transition-colors">
                <i className="ri-calendar-event-line text-2xl md:text-3xl text-blue-800 group-hover:text-white transition-colors"></i>
              </div>
              <h4 className="text-base md:text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-800 transition-colors">活動計画</h4>
              <p className="text-xs md:text-sm text-gray-600 mb-4">年間の活動予定をご確認いただけます</p>
              <div className="flex items-center text-blue-800 text-sm font-medium group-hover:translate-x-2 transition-transform">
                詳しく見る
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link 
              to="/records" 
              className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all border-2 border-gray-100 hover:border-blue-800"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-800/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-800 transition-colors">
                <i className="ri-image-line text-2xl md:text-3xl text-blue-800 group-hover:text-white transition-colors"></i>
              </div>
              <h4 className="text-base md:text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-800 transition-colors">活動記録</h4>
              <p className="text-xs md:text-sm text-gray-600 mb-4">これまでの活動の様子をご覧いただけます</p>
              <div className="flex items-center text-blue-800 text-sm font-medium group-hover:translate-x-2 transition-transform">
                詳しく見る
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link 
              to="/greeting" 
              className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all border-2 border-gray-100 hover:border-blue-800"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-800/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-800 transition-colors">
                <i className="ri-user-voice-line text-2xl md:text-3xl text-blue-800 group-hover:text-white transition-colors"></i>
              </div>
              <h4 className="text-base md:text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-800 transition-colors">会長挨拶</h4>
              <p className="text-xs md:text-sm text-gray-600 mb-4">会長からのメッセージをお読みいただけます</p>
              <div className="flex items-center text-blue-800 text-sm font-medium group-hover:translate-x-2 transition-transform">
                詳しく見る
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 border-t-4 border-blue-800">
            <div className="flex items-start gap-4 md:gap-6">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-800/10 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-2xl md:text-3xl text-blue-800"></i>
              </div>
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>お問い合わせ</h2>
                <div className="space-y-2 text-sm md:text-base text-gray-700">
                  <p>資料請求・お問い合わせは、兵庫中央岳風会まで</p>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="font-bold text-blue-800 mb-2">兵庫中央岳風会 会長 吉田岳頌</p>
                    <p className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-blue-800"></i>
                      〒653-0867 神戸市長田区高東町1-2-26
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>兵庫中央岳風会</h3>
            <p className="text-xs md:text-sm opacity-80">公益社団法人日本詩吟学院認可</p>
          </div>
          
          <nav className="mb-8 md:mb-10">
            <ul className="flex flex-wrap justify-center gap-3 md:gap-6">
              <li><Link to="/" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">トップページ</Link></li>
              <li><Link to="/about" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">会の紹介</Link></li>
              <li><Link to="/greeting" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">会長挨拶</Link></li>
              <li><Link to="/activities" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">活動計画</Link></li>
              <li><Link to="/membership" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">入会案内</Link></li>
              <li><Link to="/records" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">活動記録</Link></li>
            </ul>
          </nav>
          
          <div className="text-center text-xs md:text-sm opacity-90 border-t border-white/20 pt-6 md:pt-8">
            <p className="mb-2">© 2025 兵庫中央岳風会. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300 transition-colors whitespace-nowrap hover:underline">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
